import Dialog from "../Dialog";
import { discardChanges } from "../ConfirmProvider";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import { useConfirm } from "../../components/ConfirmProvider";
import {
  Check,
  Clock3,
  FileText,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { TASK_STATUSES, getBillingMeta } from "./taskConstants";
import {
  buildDrawerPatch,
  formatDateTime,
  formatHours,
  parseDurationToHours,
} from "./taskUtils";
import toast from "react-hot-toast";

const taskDraft = (task) => ({
  client_id: task.client_id || "", titre: task.titre || "", commentaire: task.commentaire || "", notes_internes: task.notes_internes || "",
  statut: task.statut || "a_faire", heures_estimees: task.heures_estimees == null ? "" : String(task.heures_estimees),
  taux_horaire: task.taux_horaire == null ? "" : String(task.taux_horaire), date_tache: task.date_tache || "", facturable: Boolean(task.facturable),
});

const TaskDetailsDrawer = ({
  task,
  clients,
  open,
  saving,
  deleting,
  pendingKeys = {},
  onClose,
  onSave,
  onUpdateTimeEntry,
  onDeleteTimeEntry,
  onDelete,
}) => {
  const confirm = useConfirm();
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState("");
  const currentDraft = useRef(null);
  const savedBaseline = useRef("");
  const currentTaskId = useRef(null);
  currentDraft.current = draft;
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryDraftDuration, setEntryDraftDuration] = useState("");
  const [entryDraftNote, setEntryDraftNote] = useState("");

  useEffect(() => {
    if (!task) {
      currentTaskId.current = null;
      setDraft(null);
      setEditingEntryId(null);
      setEntryDraftDuration("");
      setEntryDraftNote("");
      return;
    }
    if (currentTaskId.current === task.id && currentDraft.current && JSON.stringify(currentDraft.current) !== savedBaseline.current) return;
    const initial = taskDraft(task);
    currentTaskId.current = task.id;
    savedBaseline.current = JSON.stringify(initial);
    setDraft(initial);
    setSaveError("");
    setEditingEntryId(null);
    setEntryDraftDuration("");
    setEntryDraftNote("");
  }, [task]);

  const entry = task?.time_entries?.find((item) => item.id === editingEntryId);
  const entryDirty = !!entry && (entryDraftDuration !== String(entry.delta_heures || "") || entryDraftNote !== (entry.note || ""));
  const hasPatch = !!(task && draft && Object.keys(buildDrawerPatch(task, draft)).length);
  const dirty = !!draft && (hasPatch || entryDirty);
  useUnsavedChanges(open && (dirty || saving));
  const close = async () => {
    if (saving || deleting) return;
    if (!dirty || await confirm(discardChanges)) onClose();
  };

  if (!open || !task || !draft) {
    return null;
  }

  const billingMeta = getBillingMeta(task.statut_facturation);

  return (
    <Dialog title={task.titre} onClose={close} busy={saving || deleting} size="max-w-3xl">

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Titre
              </label>
              <input
                type="text"
                value={draft.titre}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, titre: event.target.value }))
                }
                className="input-field"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Client
                </label>
                <select
                  value={draft.client_id}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      client_id: event.target.value,
                    }))
                  }
                  className="input-field"
                >
                  <option value="">Sélectionner</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Statut
                </label>
                <select
                  value={draft.statut}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, statut: event.target.value }))
                  }
                  className="input-field"
                >
                  {TASK_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Heures estimées
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={draft.heures_estimees}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      heures_estimees: event.target.value,
                    }))
                  }
                  className="input-field"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Taux horaire
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.taux_horaire}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      taux_horaire: event.target.value,
                    }))
                  }
                  className="input-field"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Date
                </label>
                <input
                  type="date"
                  value={draft.date_tache}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      date_tache: event.target.value,
                    }))
                  }
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Commentaire
              </label>
              <textarea
                rows={4}
                value={draft.commentaire}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    commentaire: event.target.value,
                  }))
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Notes internes
              </label>
              <textarea
                rows={4}
                value={draft.notes_internes}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    notes_internes: event.target.value,
                  }))
                }
                className="input-field"
              />
            </div>

            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
              <input
                type="checkbox"
                checked={draft.facturable}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    facturable: event.target.checked,
                  }))
                }
              />
              <span className="text-sm text-slate-700">Tâche facturable</span>
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Résumé</h3>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${billingMeta.className}`}
              >
                {billingMeta.label}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm text-slate-600 sm:grid-cols-2">
              <div className="rounded-xl bg-white px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Temps suivi
                </p>
                <p className="mt-1 font-semibold text-slate-900">
                  {formatHours(task.heures_passees)}
                </p>
              </div>
              <div className="rounded-xl bg-white px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Temps facturé
                </p>
                <p className="mt-1 font-semibold text-slate-900">
                  {formatHours(task.heures_facturees)}
                </p>
              </div>
              <div className="rounded-xl bg-white px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Reste à facturer
                </p>
                <p className="mt-1 font-semibold text-slate-900">
                  {formatHours(task.heures_a_facturer)}
                </p>
              </div>
              <div className="rounded-xl bg-white px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  Dernière mise à jour
                </p>
                <p className="mt-1 font-semibold text-slate-900">
                  {formatDateTime(task.updated_at)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">
                Historique du temps
              </h3>
            </div>
            {task.time_entries?.length ? (
              <div className="space-y-3">
                {[...(task.time_entries || [])]
                  .slice()
                  .reverse()
                  .map((entry) => {
                    const isEditing = editingEntryId === entry.id;
                    const pendingKey = `drawer:${task.id}:time-entry:${entry.id}`;
                    const isSavingEntry = Boolean(pendingKeys[pendingKey]);

                    return (
                      <div
                        key={entry.id}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-slate-900">
                            {entry.delta_heures > 0 ? "+" : ""}
                            {formatHours(entry.delta_heures)}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {formatDateTime(entry.created_at)}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingEntryId(entry.id);
                                setEntryDraftDuration(
                                  String(entry.delta_heures || "")
                                );
                                setEntryDraftNote(entry.note || "");
                              }}
                              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              aria-label="Modifier l'entrée de temps"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                  !onDeleteTimeEntry ||
                                  !await confirm(
                                    "Supprimer cette entrée de temps ?"
                                  )
                                ) {
                                  return;
                                }
                                await onDeleteTimeEntry(
                                  task,
                                  entry,
                                  pendingKey
                                );
                              }}
                              className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                              aria-label="Supprimer l'entrée de temps"
                              disabled={isSavingEntry}
                            >
                              {isSavingEntry ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="mt-2 space-y-2">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-slate-500">
                                Durée
                              </label>
                              <input
                                type="text"
                                value={entryDraftDuration}
                                onChange={(event) =>
                                  setEntryDraftDuration(event.target.value)
                                }
                                className="input-field text-sm"
                                placeholder="Ex: 2h30, 45m, 1.5"
                              />
                            </div>
                            <textarea
                              rows={3}
                              value={entryDraftNote}
                              onChange={(event) =>
                                setEntryDraftNote(event.target.value)
                              }
                              className="input-field text-sm"
                              placeholder="Note interne"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingEntryId(null);
                                  setEntryDraftDuration("");
                                  setEntryDraftNote("");
                                }}
                                className="btn-secondary"
                                disabled={isSavingEntry}
                              >
                                Annuler
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!onUpdateTimeEntry) return;
                                  const parsedDuration =
                                    parseDurationToHours(entryDraftDuration);
                                  if (
                                    !Number.isFinite(parsedDuration) ||
                                    parsedDuration === 0
                                  ) {
                                    toast.error("Durée invalide");
                                    return;
                                  }
                                  await onUpdateTimeEntry(
                                    task,
                                    entry,
                                    {
                                      delta_heures: parsedDuration,
                                      note: entryDraftNote,
                                    },
                                    pendingKey
                                  );
                                  setEditingEntryId(null);
                                  setEntryDraftDuration("");
                                  setEntryDraftNote("");
                                }}
                                className="btn-primary"
                                disabled={isSavingEntry}
                              >
                                {isSavingEntry ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                                <span>Enregistrer</span>
                              </button>
                            </div>
                          </div>
                        ) : entry.note ? (
                          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-500">
                            {entry.note}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Aucune entrée de temps.</p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">
                Historique de facturation
              </h3>
            </div>
            {task.facturation_entries?.length ? (
              <div className="space-y-3">
                {[...(task.facturation_entries || [])]
                  .slice()
                  .reverse()
                  .map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold text-slate-900">
                          {entry.facture_numero}
                        </span>
                        <span>{formatHours(entry.quantite)}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-400">
                        {formatDateTime(entry.created_at)}
                      </p>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Aucune facture liée.</p>
            )}
          </div>
        </div>

        {saveError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{saveError}</p>}
        <div className="border-t border-slate-200 px-5 py-4">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete(task.id)}
              disabled={deleting || (task.facturation_entries || []).length > 0}
              className="btn-danger disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span>Supprimer</span>
            </button>

            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <button type="button" onClick={close} className="btn-secondary">
                Fermer
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!dirty) return;
                  const patch = buildDrawerPatch(task, draft);
                  if (!String(draft.titre || "").trim()) {
                    toast.error("Le titre est obligatoire");
                    return;
                  }
                  if (!draft.client_id) {
                    toast.error("Le client est obligatoire");
                    return;
                  }
                  setSaveError("");
                  try {
                    const saved = await onSave(task, patch, `drawer:${task.id}`);
                    const initial = taskDraft(saved || { ...task, ...patch });
                    savedBaseline.current = JSON.stringify(initial);
                    setDraft(initial);
                  } catch (failure) { setSaveError(failure?.response?.data?.detail || "Impossible d'enregistrer. Vos modifications sont conservées."); }
                }}
                disabled={saving || !hasPatch}
                className="btn-primary"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span>Enregistrer</span>
              </button>
            </div>
          </div>
        </div>
    </Dialog>
  );
};

export default TaskDetailsDrawer;
